import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarDays, TriangleAlert } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { type DriftedTaskView, type DriftedTasksView } from '@apm/contracts';
import { Button } from '../../../components/ui/button';
import { DatePicker } from '../../../components/ui/date-picker';
import { Popover, PopoverContent, PopoverTrigger } from '../../../components/ui/popover';
import { displayDate } from '../../../lib/date';
import { apiFetch } from '../../../lib/http';
import { errorMessage } from '../../../lib/error-message';
import { taskFormApi } from '../api';

/**
 * Việc đang chậm deadline — APM-AI-z, gọn lại theo yêu cầu người dùng 2026-09-23.
 *
 * ## Vì sao không còn nút "Áp dụng" phương án ở đây
 *
 * Bản trước bày phương án của solver ("Dàn lại lịch, giữ nguyên người", "Gỡ một
 * việc ít quan trọng") dưới từng việc, mỗi cái một nút áp một chạm. Người dùng
 * bỏ chúng: câu hỏi đầu tiên khi thấy một việc trễ là "việc gì, hạn bao giờ",
 * không phải "dời cả chuỗi phía sau thế nào". Nên mỗi dòng nay trả lời đúng hai
 * câu ấy — tiêu đề mở chi tiết việc, deadline bấm vào là sửa.
 *
 * Phương án cân bằng vẫn còn ở nút "Cân bằng" trên thanh công cụ (APM-PSET-l)
 * và ở "Xếp lịch bằng AI"; endpoint áp một chạm ở API vẫn giữ nguyên.
 *
 * ## "Trễ" đếm bằng ngày làm việc
 *
 * T2–T6, trừ ngày lễ của tổ chức — xem `detectDrift`. Hạn thứ Sáu thì sáng thứ
 * Hai là trễ **1** ngày, không phải 3.
 *
 * ## Sửa deadline ở đây là đủ để việc rời dải
 *
 * Server chọn việc vào danh sách theo `due_date < hôm nay`. Dời deadline sang
 * sau hôm nay thì lượt tải lại (invalidate ngay sau khi lưu) không còn trả nó.
 *
 * ## Không có gì chậm thì không chiếm chỗ
 *
 * Một dải "hôm nay mọi thứ đúng hạn" ở đầu tab Công việc là thứ người ta học
 * cách không nhìn trong một tuần, và rồi không nhìn cả khi nó đổi màu.
 *
 * ## Điện thoại: một hàng mảnh
 *
 * Dải đứng trên cùng tab, nên ở 390px mỗi pixel của nó là một pixel board bị
 * đẩy xuống (người dùng yêu cầu 2026-10-07). Lề và nút nhỏ lại; tiêu đề cắt
 * bằng dấu … thay vì đẩy nút xuống hàng thứ hai.
 */
export function DriftBanner({
  projectId,
  canAssign,
  canSchedule = false,
  onOpenTask,
}: {
  projectId: string;
  /** `task:assign` — không có thì không đọc và không hiện gì. */
  canAssign: boolean;
  /**
   * Lập kế hoạch được (`project:update`, tức PM) thì deadline bấm vào là sửa;
   * không thì chỉ để đọc. Dời hạn là thẩm quyền lập lịch (APM-CHANGE-b), không
   * phải `task:update` — CONTRIBUTOR có `task:update` trên việc của chính mình.
   */
  canSchedule?: boolean;
  /** Mở chi tiết việc — cùng ngăn kéo mà bảng việc bên dưới dùng. */
  onOpenTask?: (taskId: string) => void;
}): ReactNode {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);

  const drifted = useQuery({
    queryKey: ['rebalance', 'drift', projectId],
    queryFn: () => apiFetch<DriftedTasksView>(`/projects/${projectId}/rebalance/drift`),
    enabled: canAssign,
  });

  const setDue = useMutation({
    mutationFn: ({ taskId, dueDate }: { taskId: string; dueDate: string | null }) =>
      taskFormApi.update(taskId, { dueDate }),
    onSuccess: () => toast.success(t('drift.dueSaved')),
    onError: (error) => toast.error(errorMessage(error, t)),
    // Deadline đổi thì bảng việc, Gantt và chính dải này đều đang nói về nó.
    onSettled: () => queryClient.invalidateQueries(),
  });

  const tasks = openLateTasks(drifted.data);
  if (!canAssign || tasks.length === 0) return null;

  return (
    <section className="flex flex-col gap-2 rounded-DEFAULT border border-amber-300 bg-amber-50 p-3 text-sm max-sm:gap-1.5 max-sm:py-1.5 max-sm:pl-2.5 max-sm:pr-1.5 dark:border-amber-700 dark:bg-amber-950/40">
      <div className="flex items-center gap-2">
        <TriangleAlert className="size-4 shrink-0 text-amber-700 dark:text-amber-300" aria-hidden />
        <span className="min-w-0 truncate font-medium">
          {t('drift.title', { count: tasks.length })}
        </span>
        <Button
          size="sm"
          variant="outline"
          className="ml-auto shrink-0 max-sm:h-7 max-sm:px-2.5 max-sm:text-xs"
          aria-expanded={open}
          onClick={() => setOpen(!open)}
        >
          {open ? t('drift.hide') : t('drift.show')}
        </Button>
      </div>

      {open ? (
        <ul className="flex flex-col gap-1">
          {tasks.map((task) => (
            <li
              key={task.taskId}
              className="flex flex-wrap items-center gap-2 rounded-DEFAULT bg-background p-2 text-xs"
            >
              <span className="text-muted-foreground">{task.taskKey}</span>
              {onOpenTask ? (
                <button
                  type="button"
                  className="min-w-0 flex-1 truncate text-left font-medium hover:underline focus-visible:underline focus-visible:outline-none"
                  onClick={() => onOpenTask(task.taskId)}
                >
                  {task.title}
                </button>
              ) : (
                <span className="min-w-0 flex-1 truncate">{task.title}</span>
              )}
              <span className="text-rag-red">
                {t(`drift.cause.${task.cause}`, { days: task.deltaDays })}
              </span>
              <DueChip
                task={task}
                editable={canSchedule}
                saving={setDue.isPending}
                onPick={(dueDate) => setDue.mutate({ taskId: task.taskId, dueDate })}
              />
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}

/**
 * Việc **đang** chậm deadline: trễ, và **chưa hoàn thành**.
 *
 * API này trả rộng hơn thế, có chủ đích — nó phục vụ cả AI cân bằng:
 * - việc vừa xong **sớm** (để đề xuất kéo việc kế lên);
 * - việc vừa xong **trễ** trong 2 ngày gần đây (`actual` khác `null`).
 *
 * Dải này tên là "đang chậm deadline", nên cả hai loại đều nói sai ngay ở con
 * số trên tiêu đề — việc đã đóng không còn gì để xử lý (người dùng yêu cầu
 * 2026-09-30). Việc xong trễ vẫn thấy được ở chỗ của nó: hạn đỏ đậm trên thẻ
 * board, dòng danh sách và thanh Gantt (`isCompletedLate`).
 *
 * Dùng chung cho nút "Cân bằng" ở `tasks-panel` — hai chỗ lọc lệch nhau thì
 * nút hiện ra trong khi dải báo không có gì.
 */
export function openLateTasks(view: DriftedTasksView | undefined): DriftedTaskView[] {
  return (view?.tasks ?? []).filter((task) => task.cause === 'late' && task.actual === null);
}

/**
 * Deadline của một việc — bấm vào thì mở lịch để sửa, như ô "Hạn" ở bảng việc.
 *
 * Không được sửa thì vẫn hiện: câu "trễ 2 ngày" không kèm hạn là một con số
 * không ai kiểm được bằng mắt.
 */
function DueChip({
  task,
  editable,
  saving,
  onPick,
}: {
  task: DriftedTaskView;
  editable: boolean;
  saving: boolean;
  onPick: (dueDate: string | null) => void;
}): ReactNode {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const label = t('drift.due', { date: displayDate(task.dueDate) });

  if (!editable) {
    return <span className="tabular-nums text-muted-foreground">{label}</span>;
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={saving}
          aria-label={t('drift.changeDue', { key: task.taskKey })}
          className="inline-flex items-center gap-1 rounded-control px-1.5 py-0.5 tabular-nums text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-50"
        >
          <CalendarDays aria-hidden className="size-3.5" />
          {label}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-auto p-2">
        <DatePicker
          aria-label={t('drift.changeDue', { key: task.taskKey })}
          value={task.dueDate ?? ''}
          onChange={(next) => {
            if (next === '' || next === task.dueDate) return;
            onPick(next);
            setOpen(false);
          }}
        />
      </PopoverContent>
    </Popover>
  );
}
